import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Search, AlertCircle } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { formatRupees } from "../utils/currency";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import PropTypes from 'prop-types';

export default function AdminSubscriptions({ token }) {
    const [plans, setPlans] = useState([]);
    const [planStats, setPlanStats] = useState([]);
    const [expiringUsers, setExpiringUsers] = useState([]);

    // User Search State
    const [searchQuery, setSearchQuery] = useState({ name: "", email: "", phone: "" });
    const [userResults, setUserResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);

    // UI State
    const [showDialog, setShowDialog] = useState(false);
    const [editingPlan, setEditingPlan] = useState(null);
    const [showAssignDialog, setShowAssignDialog] = useState(false);

    const [formData, setFormData] = useState({
        name: "",
        price: "",
        currency: "INR",
        is_default: false,
        validity_days: "",
        quotas: {
            appointments: { resource: "appointments", limit: 0, period: "monthly" },
            patients: { resource: "patients", limit: 0, period: "monthly" },
            clinics: { resource: "clinics", limit: 1, period: "global" },
            members: { resource: "members", limit: 1, period: "global" }
        }
    });

    // User Assignment State
    const [assignUserId, setAssignUserId] = useState(""); // For manual entry or pre-fill
    const [assignUserEmail, setAssignUserEmail] = useState(""); // Display only
    const [assignPlanId, setAssignPlanId] = useState("");
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        checkAdminStatus();
    }, [token]);

    const checkAdminStatus = async () => {
        try {
            const response = await axios.get(`${API}/profile`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.type === 'global_admin') {
                setIsAdmin(true);
                fetchPlans();
                fetchStats();
            } else {
                setIsAdmin(false);
            }
        } catch (error) {
            console.error("Failed to check admin status", error);
            setIsAdmin(false);
        } finally {
            setLoading(false);
        }
    };

    const fetchPlans = async () => {
        try {
            const response = await axios.get(`${API}/subscription/plans`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPlans(response.data);
        } catch (error) {
            console.error("Failed to fetch plans", error);
            toast.error("Failed to fetch plans");
        }
    };

    const fetchStats = async () => {
        try {
            const [planStatsRes, expiringRes] = await Promise.all([
                axios.get(`${API}/subscription/admin/stats/plans`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/subscription/admin/stats/expiring`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setPlanStats(planStatsRes.data);
            setExpiringUsers(expiringRes.data);
        } catch (error) {
            console.error("Failed to fetch stats", error);
        }
    };

    const handleSearch = async (e) => {
        if (e) e.preventDefault();
        setSearchLoading(true);
        try {
            const params = new URLSearchParams();
            if (searchQuery.name) params.append("name", searchQuery.name);
            if (searchQuery.email) params.append("email", searchQuery.email);
            if (searchQuery.phone) params.append("phone", searchQuery.phone);

            const response = await axios.get(`${API}/subscription/admin/users?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUserResults(response.data.users);
        } catch (error) {
            console.error("Failed to search users", error);
            toast.error("Search failed");
        } finally {
            setSearchLoading(false);
        }
    };

    if (loading) return <div className="p-8">Loading...</div>;

    if (!isAdmin) {
        return (
            <div className="container mx-auto p-8 text-center">
                <h1 className="text-2xl font-bold text-red-500">Access Denied</h1>
                <p className="mt-4">You do not have permission to view this page.</p>
            </div>
        );
    }

    const resetForm = () => {
        setFormData({
            name: "",
            price: "",
            currency: "INR",
            is_default: false,
            validity_days: "",
            quotas: {
                appointments: { resource: "appointments", limit: 0, period: "monthly" },
                patients: { resource: "patients", limit: 0, period: "monthly" },
                clinics: { resource: "clinics", limit: 1, period: "global" },
                members: { resource: "members", limit: 1, period: "global" }
            }
        });
        setEditingPlan(null);
    };

    const handleEdit = (plan) => {
        setEditingPlan(plan);
        setFormData({
            name: plan.name,
            price: (plan.price / 100).toString(),
            currency: plan.currency,
            is_default: plan.is_default,
            validity_days: plan.validity_days ? plan.validity_days.toString() : "",
            quotas: {
                appointments: plan.quotas.appointments || { resource: "appointments", limit: 0, period: "monthly" },
                patients: plan.quotas.patients || { resource: "patients", limit: 0, period: "global" },
                clinics: plan.quotas.clinics || { resource: "clinics", limit: 1, period: "global" },
                members: plan.quotas.members || { resource: "members", limit: 1, period: "global" }
            }
        });
        setShowDialog(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                price: parseInt(parseFloat(formData.price) * 100),
                validity_days: formData.validity_days ? parseInt(formData.validity_days) : null,
            };

            if (editingPlan) {
                await axios.put(`${API}/subscription/plans/${editingPlan.id}`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success("Plan updated successfully");
            } else {
                await axios.post(`${API}/subscription/plans`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success("Plan created successfully");
            }

            setShowDialog(false);
            resetForm();
            fetchPlans();
        } catch (error) {
            console.error("Failed to save plan", error);
            toast.error("Failed to save plan");
        }
    };

    const handleDelete = async (planId) => {
        if (!window.confirm("Are you sure? This cannot be undone.")) return;
        try {
            await axios.delete(`${API}/subscription/plans/${planId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Plan deleted successfully");
            fetchPlans();
        } catch (error) {
            console.error("Failed to delete plan", error);
            toast.error("Failed to delete plan");
        }
    };

    const openAssignDialog = (userId, userEmail) => {
        setAssignUserId(userId);
        setAssignUserEmail(userEmail || userId);
        setAssignPlanId("");
        setShowAssignDialog(true);
    };

    const handleAssign = async () => {
        if (!assignUserId || !assignPlanId) {
            toast.error("Please provide User ID and Plan ID");
            return;
        }
        try {
            await axios.post(`${API}/subscription/users/${assignUserId}/plan?plan_id=${assignPlanId}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Plan assigned successfully");
            setShowAssignDialog(false);
            fetchStats(); // Refresh stats
            if (searchQuery.name || searchQuery.email || searchQuery.phone) {
                handleSearch(); // Refresh search results
            }
        } catch (error) {
            console.error("Failed to assign plan", error);
            toast.error("Failed to assign plan");
        }
    };

    const getPlanUserCount = (planId) => {
        const stat = planStats.find(s => s.plan_id === planId);
        return stat ? stat.active_users : 0;
    };

    return (
        <div className="container mx-auto p-6 space-y-8">
            <div className="flex justify-between items-center">
                <h1 className="text-3xl font-bold">Subscription Management</h1>
                <Button onClick={() => { resetForm(); setShowDialog(true); }}>
                    <Plus className="mr-2" size={20} /> New Plan
                </Button>
            </div>

            <Tabs defaultValue="overview" className="w-full">
                <TabsList className="mb-4">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="users">User Management</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Active Plans Card */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Active Plans Distribution</CardTitle>
                                <CardDescription>Overview of users per plan.</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Plan Name</TableHead>
                                            <TableHead>Price</TableHead>
                                            <TableHead>Active Users</TableHead>
                                            <TableHead className="w-[100px]">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {plans.map((plan) => (
                                            <TableRow key={plan.id}>
                                                <TableCell className="font-medium">
                                                    {plan.name}
                                                    {plan.is_default && <Badge variant="secondary" className="ml-2">Default</Badge>}
                                                </TableCell>
                                                <TableCell>{formatRupees(plan.price)}</TableCell>
                                                <TableCell>{getPlanUserCount(plan.id)}</TableCell>
                                                <TableCell>
                                                    <div className="flex gap-2">
                                                        <Button variant="ghost" size="icon" onClick={() => handleEdit(plan)}>
                                                            <Edit2 size={16} />
                                                        </Button>
                                                        <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDelete(plan.id)}>
                                                            <Trash2 size={16} />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {/* No Plan Row */}
                                        <TableRow key="no_plan">
                                            <TableCell className="font-medium">No Plan</TableCell>
                                            <TableCell>-</TableCell>
                                            <TableCell>{getPlanUserCount("no_plan")}</TableCell>
                                            <TableCell>-</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>

                        {/* Expiring Soon Card */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-orange-600 flex items-center gap-2">
                                    <AlertCircle size={20} /> Expiring Soon (7 Days)
                                </CardTitle>
                                <CardDescription>Users with subscriptions expiring this week.</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {expiringUsers.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">No expiring subscriptions.</div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>User</TableHead>
                                                <TableHead>Plan</TableHead>
                                                <TableHead>Days Left</TableHead>
                                                <TableHead>Action</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {expiringUsers.map((user) => (
                                                <TableRow key={user.user_id}>
                                                    <TableCell>
                                                        <div className="font-medium">{user.name}</div>
                                                        <div className="text-xs text-gray-500">{user.email}</div>
                                                    </TableCell>
                                                    <TableCell>{user.plan_name}</TableCell>
                                                    <TableCell className="text-orange-600 font-bold">{user.days_left} days</TableCell>
                                                    <TableCell>
                                                        <Button size="sm" variant="outline" onClick={() => openAssignDialog(user.user_id, user.email)}>
                                                            Renew
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                <TabsContent value="users">
                    <Card>
                        <CardHeader>
                            <CardTitle>User Management</CardTitle>
                            <CardDescription>Search for users and manage their subscriptions.</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Search Form */}
                            <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end bg-gray-50 p-4 rounded-lg">
                                <div className="space-y-2">
                                    <Label>Name</Label>
                                    <Input
                                        placeholder="Search by Name"
                                        value={searchQuery.name}
                                        onChange={(e) => setSearchQuery({ ...searchQuery, name: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Email</Label>
                                    <Input
                                        placeholder="Search by Email"
                                        value={searchQuery.email}
                                        onChange={(e) => setSearchQuery({ ...searchQuery, email: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Phone</Label>
                                    <Input
                                        placeholder="Search by Phone"
                                        value={searchQuery.phone}
                                        onChange={(e) => setSearchQuery({ ...searchQuery, phone: e.target.value })}
                                    />
                                </div>
                                <Button type="submit" disabled={searchLoading}>
                                    <Search className="mr-2" size={16} />
                                    {searchLoading ? "Searching..." : "Search"}
                                </Button>
                            </form>

                            {/* Results Table */}
                            {userResults.length > 0 && (
                                <div className="border rounded-md">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>User Details</TableHead>
                                                <TableHead>Current Plan</TableHead>
                                                <TableHead>Stats (Mo/Total/Clinic)</TableHead>
                                                <TableHead>Action</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {userResults.map((item) => (
                                                <TableRow key={item.user.id}>
                                                    <TableCell>
                                                        <div className="font-medium">{item.user.name}</div>
                                                        <div className="text-xs text-gray-500">{item.user.email}</div>
                                                        <div className="text-xs text-gray-500">{item.user.phone}</div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="font-medium">{item.plan.name}</div>
                                                        {item.plan.days_left !== null && (
                                                            <div className={`text-xs ${item.plan.days_left < 7 ? 'text-orange-600 font-bold' : 'text-gray-500'}`}>
                                                                {item.plan.days_left} days left
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="text-sm">
                                                            <span title="Appointments this month">Appts: {item.stats.appointments_this_month}</span> /
                                                            <span title="Total Patients"> Pts: {item.stats.total_patients}</span> /
                                                            <span title="Total Clinics"> Cln: {item.stats.total_clinics}</span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Button size="sm" onClick={() => openAssignDialog(item.user.id, item.user.email)}>
                                                            Assign Plan
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                            {userResults.length === 0 && !searchLoading && (
                                <div className="text-center py-8 text-gray-500">
                                    Search for users to see results.
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Plan CRUD Dialog */}
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingPlan ? 'Edit Plan' : 'Create New Plan'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="grid gap-6 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="name">Plan Name</Label>
                                <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                            </div>
                            <div>
                                <Label htmlFor="price">Price (INR)</Label>
                                <Input id="price" type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} required />
                            </div>
                            <div>
                                <Label htmlFor="validity_days">Validity Period (Days)</Label>
                                <Input
                                    id="validity_days"
                                    type="number"
                                    min="1"
                                    placeholder="Leave empty for unlimited"
                                    value={formData.validity_days}
                                    onChange={(e) => setFormData({ ...formData, validity_days: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="space-y-4 border rounded p-4">
                            <h3 className="font-semibold">Quotas</h3>

                            <div className="grid grid-cols-3 gap-4 items-end">
                                <Label className="col-span-1">Appointments (Monthly)</Label>
                                <Input
                                    type="number"
                                    value={formData.quotas.appointments.limit}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        quotas: { ...formData.quotas, appointments: { ...formData.quotas.appointments, limit: parseInt(e.target.value) } }
                                    })}
                                />
                            </div>
                            <div className="grid grid-cols-3 gap-4 items-end">
                                <Label className="col-span-1">Patients (Monthly)</Label>
                                <Input
                                    type="number"
                                    value={formData.quotas.patients.limit}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        quotas: { ...formData.quotas, patients: { ...formData.quotas.patients, limit: parseInt(e.target.value) } }
                                    })}
                                />
                            </div>
                            <div className="grid grid-cols-3 gap-4 items-end">
                                <Label className="col-span-1">Clinics (Global)</Label>
                                <Input
                                    type="number"
                                    value={formData.quotas.clinics.limit}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        quotas: { ...formData.quotas, clinics: { ...formData.quotas.clinics, limit: parseInt(e.target.value) } }
                                    })}
                                />
                            </div>
                            <div className="grid grid-cols-3 gap-4 items-end">
                                <Label className="col-span-1">Staff Members (Global)</Label>
                                <Input
                                    type="number"
                                    value={formData.quotas.members.limit}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        quotas: { ...formData.quotas, members: { ...formData.quotas.members, limit: parseInt(e.target.value) } }
                                    })}
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <Switch
                                id="is_default"
                                checked={formData.is_default}
                                onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
                            />
                            <Label htmlFor="is_default">Default Plan (for new users)</Label>
                        </div>

                        <Button type="submit">{editingPlan ? 'Update Plan' : 'Create Plan'}</Button>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Assign Plan Dialog */}
            <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Assign Plan to User</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div>
                            <Label>User</Label>
                            <div className="font-medium text-lg">{assignUserEmail || assignUserId}</div>
                            {assignUserEmail && <div className="text-xs text-gray-500 font-mono mt-1">{assignUserId}</div>}
                        </div>
                        <div>
                            <Label>Select Plan</Label>
                            <select
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                value={assignPlanId}
                                onChange={(e) => setAssignPlanId(e.target.value)}
                            >
                                <option value="">Select a plan...</option>
                                {plans.map(p => (
                                    <option key={p.id} value={p.id}>{p.name} - {formatRupees(p.price)}</option>
                                ))}
                            </select>
                        </div>
                        <Button onClick={handleAssign}>Confirm Assignment</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

AdminSubscriptions.propTypes = {
    token: PropTypes.string
};
